import { useCallback } from "react";

import type {
	BasicCustomMailOptionalItemOverrideInput,
	BasicCustomMailRequiredItemOverrideInput,
} from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

export type UsePlanEmailTemplatesProps = {
	hotelEmailAddress: string;
};

type Props = {
	hotelEmailAddress: string;
	atCreated: ForAutoGenerator<BasicCustomMailRequiredItemOverrideInput, "">;
	atUpdated: ForAutoGenerator<BasicCustomMailRequiredItemOverrideInput, "">;
	atCanceled: ForAutoGenerator<BasicCustomMailRequiredItemOverrideInput, "">;
	atCheckInBefore_00days: ForAutoGenerator<BasicCustomMailOptionalItemOverrideInput, "">;
	atCheckInBefore_01days: ForAutoGenerator<BasicCustomMailOptionalItemOverrideInput, "">;
	atCheckInBefore_07days: ForAutoGenerator<BasicCustomMailOptionalItemOverrideInput, "">;
	atCheckOutAfter_01days: ForAutoGenerator<BasicCustomMailOptionalItemOverrideInput, "">;

	onSetViewModel: (
		values: {
			key:
				| "atCreated"
				| "atUpdated"
				| "atCanceled"
				| "atCheckInBefore_00days"
				| "atCheckInBefore_01days"
				| "atCheckInBefore_07days"
				| "atCheckOutAfter_01days";
			value: ForAutoGenerator<
				BasicCustomMailRequiredItemOverrideInput | BasicCustomMailOptionalItemOverrideInput,
				""
			>;
		}[],
	) => void;
};

export const usePlanEmailTemplatesNext = ({
	hotelEmailAddress,
	atCreated,
	atUpdated,
	atCanceled,
	atCheckInBefore_00days,
	atCheckInBefore_01days,
	atCheckInBefore_07days,
	atCheckOutAfter_01days,
	onSetViewModel,
}: Props) => {
	// 予約作成時
	const onSetAtCreatedForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atCreated",
					value: {
						...atCreated,
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCreated, onSetViewModel],
	);
	const onSetAtCreatedForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCreated",
					value: {
						...atCreated,
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCreated, onSetViewModel],
	);

	// 予約更新時
	const onSetAtUpdatedForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atUpdated",
					value: {
						...atUpdated,
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atUpdated, onSetViewModel],
	);
	const onSetAtUpdatedForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atUpdated",
					value: {
						...atUpdated,
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atUpdated, onSetViewModel],
	);
	// 予約キャンセル時
	const onSetAtCanceledForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atCanceled",
					value: {
						...atCanceled,
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCanceled, onSetViewModel],
	);
	const onSetAtCanceledForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCanceled",
					value: {
						...atCanceled,
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCanceled, onSetViewModel],
	);

	// チェックイン当日
	const onSetAtCheckInBefore_00daysForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_00days",
					value: {
						...atCheckInBefore_00days,
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_00days, onSetViewModel],
	);
	const onSetAtCheckInBefore_00daysForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_00days",
					value: {
						...atCheckInBefore_00days,
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_00days, onSetViewModel],
	);
	const onSetAtCheckInBefore_00daysForShouldSendMail = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_00days",
					value: {
						...atCheckInBefore_00days,
						shouldSendEmail: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_00days, onSetViewModel],
	);
	// チェックイン1日前
	const onSetAtCheckInBefore_01daysForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_01days",
					value: {
						...atCheckInBefore_01days,
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_01days, onSetViewModel],
	);
	const onSetAtCheckInBefore_01daysForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_01days",
					value: {
						...atCheckInBefore_01days,
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_01days, onSetViewModel],
	);
	const onSetAtCheckInBefore_01daysForShouldSendMail = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_01days",
					value: {
						...atCheckInBefore_01days,
						shouldSendEmail: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_01days, onSetViewModel],
	);
	// チェックイン7日前
	const onSetAtCheckInBefore_07daysForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_07days",
					value: {
						...(atCheckInBefore_07days ?? {}),
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_07days, onSetViewModel],
	);
	const onSetAtCheckInBefore_07daysForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_07days",
					value: {
						...atCheckInBefore_07days,
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_07days, onSetViewModel],
	);
	const onSetAtCheckInBefore_07daysForShouldSendMail = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckInBefore_07days",
					value: {
						...atCheckInBefore_07days,
						shouldSendEmail: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckInBefore_07days, onSetViewModel],
	);
	// チェックアウト1日後
	const onSetAtCheckOutAfter_01daysForText = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "atCheckOutAfter_01days",
					value: {
						...(atCheckOutAfter_01days ?? {}),
						text: {
							value: value || "",
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckOutAfter_01days, onSetViewModel],
	);
	const onSetAtCheckOutAfter_01daysForOverride = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckOutAfter_01days",
					value: {
						...(atCheckOutAfter_01days ?? {}),
						override: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckOutAfter_01days, onSetViewModel],
	);
	const onSetAtCheckOutAfter_01daysForShouldSendMail = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "atCheckOutAfter_01days",
					value: {
						...atCheckOutAfter_01days,
						shouldSendEmail: {
							value: value || false,
							isEdited: true,
						},
					},
				},
			]);
		},
		[atCheckOutAfter_01days, onSetViewModel],
	);

	const isEdited =
		atCreated?.text?.isEdited ??
		atCreated?.override?.isEdited ??
		atUpdated?.text?.isEdited ??
		atUpdated?.override?.isEdited ??
		atCanceled?.text?.isEdited ??
		atCanceled?.override?.isEdited ??
		atCheckInBefore_07days?.text?.isEdited ??
		atCheckInBefore_07days?.override?.isEdited ??
		atCheckInBefore_07days?.shouldSendEmail?.isEdited ??
		atCheckInBefore_00days?.text?.isEdited ??
		atCheckInBefore_00days?.override?.isEdited ??
		atCheckInBefore_00days?.shouldSendEmail?.isEdited ??
		atCheckInBefore_01days?.text?.isEdited ??
		atCheckInBefore_01days?.override?.isEdited ??
		atCheckInBefore_01days?.shouldSendEmail?.isEdited ??
		atCheckOutAfter_01days?.text?.isEdited ??
		atCheckOutAfter_01days?.override?.isEdited ??
		atCheckOutAfter_01days?.shouldSendEmail?.isEdited ??
		false;

	const hasError = Boolean(
		atCreated?.text?.error ??
			atCreated?.override?.error ??
			atUpdated?.text?.error ??
			atUpdated?.override?.error ??
			atCanceled?.text?.error ??
			atCanceled?.override?.error ??
			atCheckInBefore_07days?.text?.error ??
			atCheckInBefore_07days?.override?.error ??
			atCheckInBefore_07days?.shouldSendEmail?.error ??
			atCheckInBefore_01days?.text?.error ??
			atCheckInBefore_01days?.override?.error ??
			atCheckInBefore_01days?.shouldSendEmail?.error ??
			atCheckInBefore_00days?.text?.error ??
			atCheckInBefore_00days?.override?.error ??
			atCheckInBefore_00days?.shouldSendEmail?.error ??
			atCheckOutAfter_01days?.text?.error ??
			atCheckOutAfter_01days?.override?.error ??
			atCheckOutAfter_01days?.shouldSendEmail?.error ??
			false,
	);

	return {
		hotelEmailAddress,
		isEdited,
		hasError,
		atCreated,
		onSetAtCreatedForText,
		onSetAtCreatedForOverride,
		atUpdated,
		onSetAtUpdatedForText,
		onSetAtUpdatedForOverride,
		atCanceled,
		onSetAtCanceledForText,
		onSetAtCanceledForOverride,
		atCheckInBefore_07days,
		onSetAtCheckInBefore_07daysForText,
		onSetAtCheckInBefore_07daysForOverride,
		onSetAtCheckInBefore_07daysForShouldSendMail,
		atCheckInBefore_01days,
		onSetAtCheckInBefore_01daysForText,
		onSetAtCheckInBefore_01daysForOverride,
		onSetAtCheckInBefore_01daysForShouldSendMail,
		atCheckInBefore_00days,
		onSetAtCheckInBefore_00daysForText,
		onSetAtCheckInBefore_00daysForOverride,
		onSetAtCheckInBefore_00daysForShouldSendMail,
		atCheckOutAfter_01days,
		onSetAtCheckOutAfter_01daysForText,
		onSetAtCheckOutAfter_01daysForOverride,
		onSetAtCheckOutAfter_01daysForShouldSendMail,
	};
};
